import { useEffect, useMemo } from 'react';

import { aiMimeTypes, PlanStateSchema, PlaybookAiGenerationResultSchema } from '@aline/common';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';

const PLAYBOOK_GENERATION_FENCE_REGEX = new RegExp(
  `\`\`\`${aiMimeTypes.jsonPlaybookGeneration}\\s*\\n([\\s\\S]*?)\\n\`\`\``,
  'g'
);

/**
 * Opens the org-wide PlaybookGenerationJobUpdated subscription once for a
 * thread that carries a generation card, so those cards pick up a finished,
 * failed, or cancelled job through the normalized cache without polling or a
 * subscription per card.
 *
 * The subscription is org-scoped, so it also carries generations started from
 * the editor. That is harmless for cache writes and is why nothing here drives
 * per-card behaviour off an arriving event.
 */
export function usePlaybookGenerationSubscriptions({
  messages,
}: {
  messages: Pick<Apollo.AgentMessage, 'planState' | 'text'>[];
}) {
  const client = useApolloClient();
  const hasGenerationCard = useMemo(() => getHasPlaybookGenerationCard(messages), [messages]);

  useEffect(() => {
    if (!hasGenerationCard) return;

    const subscription = client
      .subscribe<Apollo.OnPlaybookGenerationJobUpdatedSubscription>({
        query: Apollo.OnPlaybookGenerationJobUpdatedDocument,
      })
      .subscribe({
        error: () => undefined,
        next: ({ data }) => forgetDeletedGenerationJob(client, data),
      });

    return () => subscription.unsubscribe();
  }, [client, hasGenerationCard]);
}

/** Whether any message in the thread carries a playbook generation card. */
export function getHasPlaybookGenerationCard(
  messages: Pick<Apollo.AgentMessage, 'planState' | 'text'>[]
): boolean {
  return messages.some(message => {
    if (getHasGenerationFence(message.text)) return true;
    if (!message.planState) return false;

    const parsedPlanState = PlanStateSchema.safeParse(message.planState);
    if (!parsedPlanState.success) return false;

    if (getHasGenerationFence(parsedPlanState.data.plannerText)) return true;
    return Object.values(parsedPlanState.data.nodeOutputs).some(nodeOutput =>
      getHasGenerationFence(nodeOutput.displayText)
    );
  });
}

/**
 * A cancelled generation is a deleted job, and the event announcing it carries
 * no job to write. Drop the cached entry so the card asks again and renders
 * the generation as stopped instead of running on forever.
 */
function forgetDeletedGenerationJob(
  client: ApolloClient,
  data: Apollo.OnPlaybookGenerationJobUpdatedSubscription | null | undefined
) {
  const event = data?.playbookGenerationJobUpdated;
  if (!event || event.generationJob) return;

  client.cache.evict({
    id: client.cache.identify({
      __typename: 'PlaybookGenerationJob',
      id: event.generationJobId,
    }),
  });
  client.cache.gc();
}

function getHasGenerationFence(markdown: string | null | undefined) {
  if (!markdown) return false;

  for (const match of markdown.matchAll(PLAYBOOK_GENERATION_FENCE_REGEX)) {
    const [, rawJson] = match;
    if (!rawJson) continue;
    if (parseGenerationJson(rawJson).success) return true;
  }

  return false;
}

function parseGenerationJson(rawJson: string) {
  try {
    return PlaybookAiGenerationResultSchema.safeParse(JSON.parse(rawJson));
  } catch {
    return { success: false } as const;
  }
}
